import { useEffect, useRef, useState } from 'react';

import {
    HERO_CTA_HANDOVER_OFFSET_PX,
    PRODUCT_HERO_CTA_SLOT_ID,
} from './product-hero-cta-slot';

interface HeroCtaHandover {
    /** Attach to the in-flow (summary row) copy of the actions. */
    bottomRef: React.RefObject<HTMLDivElement | null>;
    /** The hero slot, once mounted — null until then, and on mobile. */
    slotEl: HTMLElement | null;
    /** Show the hero copy. */
    heroVisible: boolean;
    /** Show the summary-row copy. */
    bottomVisible: boolean;
}

/**
 * Hands the product's actions between the hero and the summary row, so exactly
 * one copy is ever on screen.
 *
 * The hero copy holds while its slot is still below the handover line and gives
 * way as it slides up under the header; the summary copy takes over once it
 * comes into view. Exclusivity is not left to the two thresholds happening not
 * to overlap — the summary copy wins outright, and the hero copy is only shown
 * when the summary one is not, so a short page (where both could qualify at
 * once) still shows one set rather than two.
 */
export const useHeroCtaHandover = (): HeroCtaHandover => {
    const bottomRef = useRef<HTMLDivElement | null>(null);
    const [slotEl, setSlotEl] = useState<HTMLElement | null>(null);
    // null until the observer first reports. Server-rendered and pre-hydration
    // markup shows the summary copy (there is no slot yet), and resolving the
    // slot flips `bottomVisible` a commit before the observer can say whether
    // the row is on screen — starting at `false` there hid the summary copy and
    // showed the hero one for that commit, which on a short page or a deep link
    // is a visible flicker between the two.
    const [bottomInView, setBottomInView] = useState<boolean | null>(null);
    const [heroAboveLine, setHeroAboveLine] = useState(false);

    // The slot lives in a sibling section; both mount in the same commit, so it
    // is present by the time effects run.
    useEffect(() => {
        setSlotEl(document.getElementById(PRODUCT_HERO_CTA_SLOT_ID));
    }, []);

    useEffect(() => {
        const el = bottomRef.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            ([entry]) => setBottomInView(entry?.isIntersecting ?? false),
            { threshold: 0 },
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!slotEl) return;
        // Negative top margin pulls the root's top edge down, so the slot stops
        // intersecting once it is within the handover offset of the viewport top
        // — "almost at the top" without a scroll listener.
        const observer = new IntersectionObserver(
            ([entry]) => setHeroAboveLine(!(entry?.isIntersecting ?? false)),
            { rootMargin: `-${HERO_CTA_HANDOVER_OFFSET_PX}px 0px 0px 0px` },
        );
        observer.observe(slotEl);
        return () => observer.disconnect();
    }, [slotEl]);

    // Unmeasured reads as "the summary row holds them", matching what the
    // server sent.
    const bottomHolds = bottomInView ?? true;

    return {
        bottomRef,
        slotEl,
        heroVisible: !heroAboveLine && !bottomHolds,
        // Without a slot (mobile) there is no second copy to trade with, so the
        // in-flow actions are simply always there.
        bottomVisible: !slotEl || bottomHolds,
    };
};
